import type { UploadFetch } from "@/upload";
import { getCrsInfo } from "@/crs";
import { WGS84_EPSG } from "@/utils";
import proj4 from "proj4";

function epsgCode(part: string): string | null {
    const code = part.trim().replace(/^epsg\s*:?\s*/i, "");
    return /^\d+$/.test(code) ? code : null;
}

/**
 * The API takes only what PROJ reads, so the loose spellings are rewritten here rather than there: a
 * bare `3067`, a bare compound `3067+3900`, and whitespace or a repeated `EPSG:` around the `+` all
 * become `EPSG:NNNN` or `EPSG:NNNN+NNNN`. Anything not made only of EPSG codes is left alone, which
 * is what passes a proj4 string, whose parts start with `+`, through untouched.
 */
export function normalizeInputProjection(inputProjection: string) {
    const codes = inputProjection.split("+").map(epsgCode);
    if (codes.length <= 2 && !codes.includes(null)) {
        return `EPSG:${codes.join("+")}`;
    }

    return inputProjection.trim();
}

/** Run on the output of `normalizeInputProjection`, so only the canonical spelling has to pass. */
export function validateInputProjection(inputProjection: string) {
    return /^EPSG:\d+(\+\d+)?$/.test(inputProjection) || /\+proj=/.test(inputProjection);
}

export async function resolveProj4Projection(inputProjection: string, request?: UploadFetch): Promise<string> {
    const projection = normalizeInputProjection(inputProjection);
    try {
        proj4(projection, WGS84_EPSG, [0, 0]);
        return projection;
    } catch {
        const epsg = /^EPSG:(\d+)/.exec(projection)?.[1];
        if (!epsg) throw new Error("Source projection is not recognized");

        const info = await getCrsInfo(epsg, request);
        if (!info?.proj4_string?.includes("+proj=")) throw new Error(`Could not load EPSG:${epsg}`);

        return info.proj4_string;
    }
}

export function hasVerticalDatum(inputProjection: string) {
    return /^EPSG:\d+\+\d+$/.test(inputProjection);
}

/**
 * The CRS a file is in when its heights are ellipsoidal: the same horizontal, with any vertical
 * replaced. Dropping the vertical is not enough, since a CRS that names none leaves the vertical
 * datum unknown, and heights of unknown datum are rescaled rather than transformed.
 */
export function ellipsoidalInputProjection(inputProjection: string) {
    const horizontal = /^(EPSG:\d+)\+\d+$/.exec(inputProjection);
    return `${horizontal ? horizontal[1] : inputProjection}+ellipsoidal`;
}
